import { useCallback, useMemo, useState } from 'react';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useShowAlert } from 'hooks';
import isNil from 'lodash/isNil';
import strings from 'strings';
import { getErrorMessage } from 'UI/utils';

import { ratePresentationMakerGenerated } from '../services';

const buildFormParams = formData => ({
  instructions: formData?.instructions || '',
  tone: formData?.tone?.title || '',
  audience: formData?.audience?.title || '',
  length: formData?.length?.title || ''
});

export const usePresentationMaker = ({
  buildSourceParamsFn,
  extendedParams,
  generatePresentationFn,
  sourceData
}) => {
  const { showAlert } = useShowAlert();
  const [generatedAiDataMap, setGeneratedAiDataMap] = useState({});
  const [uiState, setUiState] = useState({
    isLoading: false
  });
  const regenerateId = generatedAiDataMap[extendedParams?.type]?.id;
  const currentGeneratedContent = generatedAiDataMap[extendedParams?.type]?.generatedContent;
  const currentGeneratedRating = generatedAiDataMap[extendedParams?.type]?.rating;

  const sourceParams = useMemo(
    () => buildSourceParamsFn(sourceData),
    [buildSourceParamsFn, sourceData]
  );

  const handleGenerateWithAi = useCallback(
    async formData => {
      try {
        const type = extendedParams?.type;
        if (!type) return null;

        setUiState(prev => ({ ...prev, isLoading: true }));

        const finalParams = {
          ...sourceParams,
          ...buildFormParams(formData),
          ...extendedParams,
          regenerateId
        };

        const { data } = await generatePresentationFn(finalParams);
        const formattedData = { id: data?.id, generatedContent: data?.variant };
        setGeneratedAiDataMap(prev => ({
          ...prev,
          [type]: formattedData
        }));
        return formattedData;
      } catch (error) {
        showAlert({
          body: getErrorMessage(error, strings.ai.generators.presentationsMaker.error.body),
          title: strings.ai.generators.presentationsMaker.error.title,
          severity: 'error'
        });

        throw error;
      } finally {
        setUiState(prev => ({ ...prev, isLoading: false }));
      }
    },
    [extendedParams, sourceParams, regenerateId, generatePresentationFn, showAlert]
  );

  const handleClickRateButton = useCallback(
    async ({ id, rating }) => {
      const type = extendedParams?.type;
      if (!id || !type) return false;

      const showRatingErrorAlert = error => {
        showAlert({
          title: strings.ai.generators.presentationsMaker.error.rate.title,
          body: getErrorMessage(
            error || null,
            strings.ai.generators.presentationsMaker.error.rate.message
          ),
          severity: 'error'
        });
      };

      try {
        const result = await ratePresentationMakerGenerated({ id, score: rating });
        if (
          [HTTPStatusCodes.Ok, HTTPStatusCodes.Created, HTTPStatusCodes.NoContent].includes(
            result.status
          )
        ) {
          setGeneratedAiDataMap(prev => ({
            ...prev,
            [type]: {
              id: regenerateId,
              generatedContent: currentGeneratedContent,
              rating: !isNil(rating) ? rating : currentGeneratedRating
            }
          }));
          return true;
        }
        showRatingErrorAlert(null);
        return false;
      } catch (error) {
        showRatingErrorAlert(error);
        return false;
      }
    },
    [currentGeneratedContent, currentGeneratedRating, extendedParams.type, regenerateId, showAlert]
  );

  return {
    generatedAiData: generatedAiDataMap[extendedParams?.type] || null,
    generatedAiDataMap,
    handleClickRateButton,
    handleGenerateWithAi,
    uiState
  };
};
